import React, { useEffect, useRef, createElement } from 'react';
declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: any;
  }
}
// Country code → Google Translate language code
const COUNTRY_TO_LANG: Record<string, string> = {
  CN: 'zh-CN',
  TW: 'zh-TW',
  HK: 'zh-TW',
  FR: 'fr',
  CD: 'fr',
  CI: 'fr',
  CM: 'fr',
  SN: 'fr',
  ML: 'fr',
  BF: 'fr',
  MG: 'fr',
  NE: 'fr',
  TD: 'fr',
  GN: 'fr',
  RW: 'fr',
  BE: 'fr',
  CH: 'fr',
  SA: 'ar',
  EG: 'ar',
  MA: 'ar',
  DZ: 'ar',
  TN: 'ar',
  LY: 'ar',
  SD: 'ar',
  IQ: 'ar',
  JO: 'ar',
  LB: 'ar',
  AE: 'ar',
  QA: 'ar',
  KW: 'ar',
  BH: 'ar',
  OM: 'ar',
  YE: 'ar',
  BR: 'pt',
  PT: 'pt',
  MZ: 'pt',
  AO: 'pt',
  ES: 'es',
  MX: 'es',
  AR: 'es',
  CO: 'es',
  PE: 'es',
  CL: 'es',
  EC: 'es',
  VE: 'es',
  GT: 'es',
  CU: 'es',
  BO: 'es',
  DO: 'es',
  HN: 'es',
  PY: 'es',
  SV: 'es',
  NI: 'es',
  CR: 'es',
  PA: 'es',
  UY: 'es',
  DE: 'de',
  AT: 'de',
  JP: 'ja',
  KR: 'ko',
  IN: 'hi',
  TZ: 'sw',
  ET: 'am',
  SO: 'so',
  RU: 'ru',
  BY: 'ru',
  KZ: 'ru',
  TR: 'tr',
  TH: 'th',
  VN: 'vi',
  ID: 'id',
  MY: 'ms',
  IT: 'it',
  NL: 'nl',
  PL: 'pl',
  UA: 'uk',
  SE: 'sv'
};
const ENGLISH_COUNTRIES = new Set(['US', 'GB', 'CA', 'AU', 'NZ', 'IE', 'KE', 'NG', 'GH', 'ZA', 'UG', 'ZW', 'BW', 'JM', 'TT', 'PH', 'SG']);
function getTargetLang(countryCode: string): string | null {
  if (ENGLISH_COUNTRIES.has(countryCode)) return null;
  return COUNTRY_TO_LANG[countryCode] || null;
}
// Programmatically trigger translation by selecting language in the dropdown
function triggerTranslation(lang: string) {
  const maxAttempts = 30;
  let attempts = 0;
  const trySelect = () => {
    attempts++;
    const select = document.querySelector('#google_translate_element select') as HTMLSelectElement;
    if (select) {
      // Find the option matching the target language
      const options = Array.from(select.options);
      const targetOption = options.find(opt => opt.value === lang);
      if (targetOption) {
        select.value = lang;
        // Dispatch change event to trigger Google Translate
        select.dispatchEvent(new Event('change', {
          bubbles: true
        }));
        console.log(`[AutoTranslate] Translated to: ${lang}`);
        return;
      }
    }
    // Retry — Google Translate may not have rendered the select yet
    if (attempts < maxAttempts) {
      setTimeout(trySelect, 300);
    }
  };
  trySelect();
}
function setTranslateCookie(lang: string) {
  const value = `/en/${lang}`;
  document.cookie = `googtrans=${value};path=/`;
  document.cookie = `googtrans=${value};path=/;domain=${window.location.hostname}`;
}
export function GoogleTranslateAutoInit() {
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const alreadyHandled = sessionStorage.getItem('belo_translate_handled');
    const initTranslate = (targetLang: string | null) => {
      sessionStorage.setItem('belo_translate_handled', 'true');
      if (!targetLang) return; // English country, no translation needed
      // Set cookie as backup
      setTranslateCookie(targetLang);
      // Define init callback
      window.googleTranslateElementInit = () => {
        if (window.google?.translate?.TranslateElement) {
          new window.google.translate.TranslateElement({
            pageLanguage: 'en',
            autoDisplay: false,
            includedLanguages: Object.values(COUNTRY_TO_LANG).filter((v, i, a) => a.indexOf(v) === i).join(',')
          }, 'google_translate_element');
          // After init, programmatically select the language
          triggerTranslation(targetLang);
        }
      };
      // Load script
      if (!document.getElementById('google-translate-script')) {
        const script = document.createElement('script');
        script.id = 'google-translate-script';
        script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async = true;
        document.body.appendChild(script);
      } else {
        // Script already loaded, just trigger
        triggerTranslation(targetLang);
      }
    };
    // If already handled this session, re-trigger with cookie
    if (alreadyHandled) {
      // Check if there's a stored target language
      const storedLang = sessionStorage.getItem('belo_translate_lang');
      if (storedLang) {
        setTranslateCookie(storedLang);
        window.googleTranslateElementInit = () => {
          if (window.google?.translate?.TranslateElement) {
            new window.google.translate.TranslateElement({
              pageLanguage: 'en',
              autoDisplay: false
            }, 'google_translate_element');
            triggerTranslation(storedLang);
          }
        };
        if (!document.getElementById('google-translate-script')) {
          const script = document.createElement('script');
          script.id = 'google-translate-script';
          script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
          script.async = true;
          document.body.appendChild(script);
        }
      }
      return;
    }
    // Get country and translate
    const resolveAndTranslate = (countryCode: string) => {
      const lang = getTargetLang(countryCode);
      if (lang) {
        sessionStorage.setItem('belo_translate_lang', lang);
      }
      initTranslate(lang);
    };
    // Try cached geo first
    const cached = sessionStorage.getItem('belo_geo');
    if (cached) {
      try {
        const geo = JSON.parse(cached);
        if (geo.countryCode) {
          resolveAndTranslate(geo.countryCode);
          return;
        }
      } catch {}
    }
    // Fetch geo data
    fetch('https://ipapi.co/json/').then(res => res.json()).then(data => {
      if (data.country_code) {
        resolveAndTranslate(data.country_code);
      }
    }).catch(() => {});
  }, []);
  // Position off-screen instead of display:none (display:none prevents Google Translate from initializing)
  return <div id="google_translate_element" style={{
    position: 'absolute',
    top: '-9999px',
    left: '-9999px',
    opacity: 0,
    pointerEvents: 'none'
  }} data-id="element-74" />;
}